/* ===========================================================================
 * 08 Left Reviews \u2014 storefront widget  <eight-left-reviews>
 * ---------------------------------------------------------------------------
 * Self-contained custom element (Shadow DOM, zero dependencies). Renders an
 * approved-reviews summary + list for a product, or the store-wide wall.
 *
 * Usage (added by the Liquid snippets):
 *   <eight-left-reviews
 *       api-base="https://mxzgicvmxmgrhzlvomim.supabase.co/functions/v1"
 *       product-id="{{ product.id }}"        // omit for the store wall
 *       per-page="8"></eight-left-reviews>
 *
 * Local preview: set window.__EIGHT_LEFT_REVIEWS_MOCK__ = {\u2026} to render
 * sample data without a network call.
 * ======================================================================== */
(function () {
  "use strict";
  if (customElements.get("eight-left-reviews")) return;

  // Bump on each change so you can confirm the theme has the latest copy
  // (check the browser console). Always-show-summary + 2-decimal avg + sort attr.
  const WIDGET_VERSION = "2026-10-30.1-ascii";
  console.log("%c[08L reviews] widget " + WIDGET_VERSION, "color:#0a0a0a;background:#f9cb0c;padding:2px 6px;border-radius:3px;font-weight:700");

  const INK = "#0a0a0a";       // black
  const WHITE = "#ffffff";     // white
  const YELLOW = "#f9cb0c";    // 08 Left yellow
  const RED = "#c42126";
  const FONT = '"proxima-nova","Proxima Nova",-apple-system,BlinkMacSystemFont,"Helvetica Neue",Helvetica,Arial,sans-serif';

  const STYLE = `
    :host { --ink:${INK}; --white:${WHITE}; --yellow:${YELLOW}; --red:${RED};
      display:block; color:var(--ink);
      font-family:${FONT};
      -webkit-font-smoothing:antialiased; }
    * { box-sizing:border-box; }
    a { color:inherit; }
    .wrap { max-width:820px; margin:0 auto; }

    /* Boarding-pass summary header \u2014 yellow score panel, white bars panel */
    .summary { display:flex; gap:0; border:1.5px solid var(--ink); background:var(--white); }
    .sum-score { flex:0 0 168px; padding:22px 20px; text-align:center; background:var(--yellow); color:var(--ink);
      border-right:1.5px dashed var(--ink); display:flex; flex-direction:column; justify-content:center; }
    .avg { font-size:46px; line-height:1; font-weight:800; letter-spacing:-1px; }
    .avg small { font-size:18px; font-weight:600; opacity:.55; }
    .sum-stars { margin:7px 0 4px; }
    .sum-count { font-size:11px; letter-spacing:.14em; text-transform:uppercase; opacity:.75; font-weight:600; }
    .sum-bars { flex:1; padding:18px 22px; display:flex; flex-direction:column; justify-content:center; gap:5px; }
    .bar-row { display:flex; align-items:center; gap:9px; font-size:12px; }
    .bar-row .n { width:10px; text-align:right; opacity:.7; font-variant-numeric:tabular-nums; }
    .bar-track { flex:1; height:7px; background:rgba(10,10,10,.09); position:relative; }
    .bar-fill { position:absolute; inset:0 auto 0 0; background:var(--ink); }

    /* MOBILE (Ryan 2026-08-02: "reviews not centered"). The boarding-pass
       summary is a two-column flex - a 168px yellow score panel beside the
       bars. On a 375px phone that left both columns around 170px wide and the
       whole thing read as off-centre. Below 600px it becomes one centred
       stack: the score panel full width with a dashed rule underneath instead
       of beside, and the bar chart centred under it. */
    @media (max-width:600px){
      .summary { flex-direction:column; }
      .sum-score { flex:1 1 auto; width:100%; border-right:none;
        border-bottom:1.5px dashed var(--ink); padding:18px 16px; }
      .sum-bars { width:100%; padding:16px 18px; }
      .bar-row { justify-content:center; }
      .bar-track { max-width:220px; }
      /* The two CTAs under the summary were still hugging the left edge -
         .actions is a wrap-flex, so on a narrow screen they stack but stay
         left-aligned. Centre them and let each take the full width so they
         read as a deliberate pair rather than two ragged buttons. */
      .actions { justify-content:center; }
      .actions > * { flex:1 1 100%; text-align:center; }
    }
    .bar-row .c { width:30px; text-align:right; opacity:.5; font-variant-numeric:tabular-nums; }

    /* Stars (black on white / black on yellow \u2014 always legible) */
    .stars { display:inline-flex; gap:2px; vertical-align:middle; }
    .stars svg { width:15px; height:15px; display:block; }
    .stars.lg svg { width:17px; height:17px; }
    .star-on { fill:var(--ink); }
    .star-off { fill:rgba(10,10,10,.16); }
    .sum-score .star-off { fill:rgba(10,10,10,.22); }

    /* Toolbar */
    .toolbar { display:flex; align-items:center; justify-content:space-between;
      margin:22px 2px 6px; gap:12px; flex-wrap:wrap; }
    .toolbar h3 { font-size:13px; letter-spacing:.16em; text-transform:uppercase; margin:0; font-weight:700; }
    select { font:inherit; font-size:12px; padding:6px 26px 6px 10px; border:1.5px solid var(--ink);
      background:var(--white); color:var(--ink); border-radius:0; cursor:pointer;
      -webkit-appearance:none; appearance:none;
      background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6'><path d='M0 0l5 6 5-6z' fill='%230a0a0a'/></svg>");
      background-repeat:no-repeat; background-position:right 9px center; }

    /* Review card */
    .list { display:flex; flex-direction:column; }
    .rev { padding:20px 2px; border-top:1px solid rgba(10,10,10,.14); }
    .rev:first-child { border-top:1.5px solid var(--ink); }
    .rev-head { display:flex; align-items:baseline; justify-content:space-between; gap:12px; }
    .rev-who { display:flex; align-items:center; gap:9px; flex-wrap:wrap; }
    .rev-name { font-weight:700; font-size:14px; }
    .verified { display:inline-flex; align-items:center; gap:4px; font-size:9.5px; letter-spacing:.1em;
      text-transform:uppercase; font-weight:600; color:var(--ink); border:1px solid var(--ink);
      background:rgba(249,203,12,.28); padding:1.5px 5px; border-radius:2px; }
    .verified svg { width:9px; height:9px; fill:var(--ink); }
    .rev-date { font-size:11px; opacity:.5; white-space:nowrap; font-variant-numeric:tabular-nums; }
    .rev-stars { margin:9px 0 7px; }
    .rev-title { font-weight:700; font-size:14.5px; margin:0 0 4px; }
    .rev-body { font-size:14px; line-height:1.55; margin:0; white-space:pre-wrap; opacity:.92; }
    .rev-loc { margin-top:9px; font-size:10.5px; letter-spacing:.13em; text-transform:uppercase; opacity:.5; }
    .rev-loc::before { content:"\u2708 "; opacity:.7; }
    .photos { display:flex; gap:7px; margin-top:11px; flex-wrap:wrap; }
    .photos a { width:62px; height:62px; border:1px solid rgba(10,10,10,.2); overflow:hidden; display:block; }
    .photos img { width:100%; height:100%; object-fit:cover; display:block; }
    .reply { margin:11px 0 0 0; padding:11px 14px; background:rgba(10,10,10,.035);
      border-left:3px solid var(--yellow); }
    .reply .who { font-size:10px; letter-spacing:.13em; text-transform:uppercase; font-weight:700; margin-bottom:4px; }
    .reply p { margin:0; font-size:13px; line-height:1.5; white-space:pre-wrap; opacity:.88; }

    .more { display:block; margin:20px auto 0; font:inherit; font-size:12px; letter-spacing:.13em;
      text-transform:uppercase; font-weight:700; padding:11px 26px; border:1.5px solid var(--ink);
      background:var(--white); color:var(--ink); cursor:pointer; transition:background .12s,color .12s; }
    .more:hover { background:var(--yellow); color:var(--ink); }
    .more[disabled] { opacity:.4; cursor:default; }

    /* Collapsed (product page) reveal button \u2014 sits inline with "Write a review" */
    .reveal { display:inline-block; margin:0; font:inherit; font-size:12px; letter-spacing:.13em;
      text-transform:uppercase; font-weight:700; padding:11px 26px; border:1.5px solid var(--ink);
      background:var(--white); color:var(--ink); cursor:pointer; transition:background .12s,color .12s; }
    .reveal:hover { background:var(--yellow); color:var(--ink); }

    /* Actions row + write-a-review */
    .actions { display:flex; gap:10px; flex-wrap:wrap; align-items:center; margin-top:16px; }
    .write { font:inherit; font-size:12px; letter-spacing:.13em; text-transform:uppercase; font-weight:700;
      padding:11px 26px; border:1.5px solid var(--ink); background:var(--ink); color:var(--white);
      cursor:pointer; transition:background .12s,color .12s; }
    .write:hover { background:var(--yellow); color:var(--ink); }
    .firstrev { display:flex; gap:14px; align-items:center; flex-wrap:wrap; padding:6px 0; }
    .firstrev-label { font-size:13px; opacity:.6; }
    .ff-h { font-size:20px; font-weight:800; letter-spacing:-.3px; margin:0 0 2px; }
    .formhost { margin-top:18px; }
    .form { border:1.5px solid var(--ink); background:var(--white); padding:20px; }
    .form h4 { margin:0 0 14px; font-size:13px; letter-spacing:.14em; text-transform:uppercase; }
    .field { margin-bottom:12px; }
    .field label { display:block; font-size:11px; letter-spacing:.08em; text-transform:uppercase;
      margin-bottom:5px; opacity:.7; font-weight:600; }
    .field input, .field textarea { width:100%; font:inherit; font-size:14px; padding:9px 11px;
      border:1.5px solid var(--ink); background:var(--white); color:var(--ink); border-radius:0; }
    .field textarea { min-height:96px; resize:vertical; }
    .starpick { display:inline-flex; gap:4px; cursor:pointer; }
    .starpick svg { width:27px; height:27px; }
    .hp { position:absolute !important; left:-9999px !important; width:1px; height:1px; overflow:hidden; }
    .form-actions { display:flex; gap:14px; align-items:center; margin-top:6px; }
    .submit { font:inherit; font-size:12px; letter-spacing:.13em; text-transform:uppercase; font-weight:700;
      padding:11px 28px; border:1.5px solid var(--ink); background:var(--yellow); color:var(--ink); cursor:pointer; }
    .submit:hover { background:var(--ink); color:var(--white); }
    .submit[disabled] { opacity:.5; cursor:default; }
    .cancel { font:inherit; font-size:12px; background:none; border:0; cursor:pointer;
      text-decoration:underline; color:var(--ink); opacity:.6; }
    .msg { font-size:13px; margin-top:10px; min-height:1px; }
    .msg.bad { color:var(--red); }
    .thanks { border:1.5px solid var(--ink); background:rgba(249,203,12,.16); padding:24px; text-align:center; }
    .thanks h4 { margin:0 0 6px; font-size:15px; letter-spacing:0; text-transform:none; }
    .thanks p { margin:0; font-size:13px; opacity:.82; }

    .empty { border:1.5px dashed var(--ink); padding:34px; text-align:center; opacity:.6; font-size:14px; }
    .skel { padding:40px 2px; text-align:center; opacity:.4; font-size:12px; letter-spacing:.14em; text-transform:uppercase; }
    .err { border:1.5px solid var(--red); color:var(--red); padding:16px; font-size:13px; }
  `;

  const STAR =
    '<svg viewBox="0 0 20 20" class="%C"><path d="M10 1.5l2.6 5.3 5.9.86-4.25 4.14 1 5.86L10 15.1l-5.25 2.76 1-5.86L1.5 7.66l5.9-.86z"/></svg>';

  function starsHTML(rating, big) {
    let out = `<span class="stars${big ? " lg" : ""}" aria-label="${rating} out of 5">`;
    for (let i = 1; i <= 5; i++) out += STAR.replace("%C", i <= Math.round(rating) ? "star-on" : "star-off");
    return out + "</span>";
  }
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  function fmtDate(iso) {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
  function locShort(loc) {
    if (!loc) return "";
    const p = loc.split(",").map((s) => s.trim());
    return p.length >= 2 ? `${p[0]}, ${p[1]}` : p[0];
  }

  class EightLeftReviews extends HTMLElement {
    connectedCallback() {
      this.root = this.attachShadow({ mode: "open" });
      this.apiBase = (this.getAttribute("api-base") || "").replace(/\/$/, "");
      const urlParams = new URLSearchParams(location.search);
      this.productId = this.getAttribute("product-id") ||
        urlParams.get("product") || urlParams.get("product_id") || null;
      this.perPage = parseInt(this.getAttribute("per-page") || "8", 10) || 8;
      this.collapsed = this.hasAttribute("collapsed");  // summary-only until "read all" clicked
      this.formFirst = this.hasAttribute("form-first"); // dedicated "leave a review" page
      this.scope = this.getAttribute("scope") || "";    // "all" => show ALL reviews (ignore product-id for display; submit still ties to product)
      this.expanded = false;
      this.sort = this.getAttribute("sort") || "recent";  // recent | rating_high | rating_low | photos
      this.page = 1;
      this.reviews = [];
      this.summary = null;
      this.hasMore = false;
      this.root.innerHTML = `<style>${STYLE}</style><div class="wrap"></div>`;
      this.wrap = this.root.querySelector(".wrap");

      if (this.formFirst) {
        // Submission page: show the form straight away, no reviews fetch.
        const heading = this.getAttribute("heading") || "Leave a review";
        this.wrap.innerHTML = `<div class="ff-h">${esc(heading)}</div><div class="formhost"></div>`;
        this.toggleForm();
        return;
      }
      this.wrap.innerHTML = `<div class="skel">Loading reviews\u2026</div>`;
      this.load(true);
    }

    async fetchPage() {
      const mock = window.__EIGHT_LEFT_REVIEWS_MOCK__;
      if (mock) return typeof mock === "function" ? mock(this) : mock;
      const u = new URL(this.apiBase + "/reviews-get");
      // scope="all" => omit product_id so the API returns the whole review pool.
      if (this.productId && this.scope !== "all") u.searchParams.set("product_id", this.productId);
      u.searchParams.set("page", String(this.page));
      u.searchParams.set("per_page", String(this.perPage));
      u.searchParams.set("sort", this.sort);
      const r = await fetch(u, { headers: { accept: "application/json" } });
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }

    async load(reset) {
      if (reset) { this.page = 1; this.reviews = []; }
      try {
        const data = await this.fetchPage();
        this.summary = data.summary;
        this.hasMore = data.has_more;
        this.reviews = reset ? data.reviews : this.reviews.concat(data.reviews);
        this.render();
        this.injectJsonLd();
      } catch (e) {
        this.wrap.innerHTML = `<div class="err">Reviews are temporarily unavailable.</div>`;
        console.error("[08L reviews]", e);
      }
    }

    render() {
      const s = this.summary || { count: 0, average: 0, distribution: {} };
      const heading = this.productId ? "Reviews" : "What travelers say";
      const hasReviews = s.count > 0;
      this.style.display = "";
      const max = Math.max(1, ...[5, 4, 3, 2, 1].map((n) => s.distribution[n] || 0));
      const bars = [5, 4, 3, 2, 1].map((n) => {
        const c = s.distribution[n] || 0;
        return `<div class="bar-row"><span class="n">${n}</span>
          <span class="bar-track"><span class="bar-fill" style="width:${(c / max) * 100}%"></span></span>
          <span class="c">${c}</span></div>`;
      }).join("");

      const summaryHTML = `
        <div class="summary">
          <div class="sum-score">
            <div class="avg">${s.average.toFixed(2)}<small>/5</small></div>
            <div class="sum-stars">${starsHTML(s.average, true)}</div>
            <div class="sum-count">${s.count} review${s.count === 1 ? "" : "s"}</div>
          </div>
          <div class="sum-bars">${bars}</div>
        </div>`;

      const listHTML = `
        <div class="toolbar">
          <h3>${esc(heading)}</h3>
          <select class="sortsel">
            <option value="recent">Most recent</option>
            <option value="rating_high">Highest rated</option>
            <option value="rating_low">Lowest rated</option>
            <option value="photos">With photos</option>
          </select>
        </div>
        <div class="list">${this.reviews.map((r) => this.card(r)).join("")}</div>
        ${this.hasMore ? `<button class="more">Load more reviews</button>` : ""}`;

      const revealHTML = `<button class="reveal">${
        s.count === 1 ? "Read the review" : "Read all " + s.count + " reviews"} \u2192</button>`;

      // Collapsed product widget: show only the summary graphic until the
      // shopper opts in, then expand the full list inline. With zero reviews,
      // show the summary graphic + write button only (no reveal, no list).
      const showList = hasReviews && (!this.collapsed || this.expanded);
      const actionsHTML = `<div class="actions">
        <button class="write">Write a review</button>
        ${(hasReviews && this.collapsed && !this.expanded) ? revealHTML : ""}
      </div>`;
      this.wrap.innerHTML = summaryHTML + actionsHTML + `<div class="formhost"></div>` + (showList ? listHTML : "");

      this.wrap.querySelector(".write").addEventListener("click", () => this.toggleForm());
      const reveal = this.wrap.querySelector(".reveal");
      if (reveal) reveal.addEventListener("click", () => { this.expanded = true; this.render(); });
      if (showList) {
        const sel = this.wrap.querySelector(".sortsel");
        sel.value = this.sort;
        sel.addEventListener("change", (e) => { this.sort = e.target.value; this.load(true); });
        const more = this.wrap.querySelector(".more");
        if (more) more.addEventListener("click", () => { this.page++; this.load(false); });
      }
    }

    // --- Write a review --------------------------------------------------
    toggleForm() {
      const host = this.wrap.querySelector(".formhost");
      if (!host) return;
      if (host.dataset.open === "1") { host.innerHTML = ""; host.dataset.open = ""; return; }
      host.dataset.open = "1";
      this.formRating = 0;
      host.innerHTML = `
        <div class="form">
          <h4>${this.productId ? "Review this product" : "Write a review"}</h4>
          <div class="field">
            <label>Your rating</label>
            <div class="starpick" role="radiogroup" aria-label="Rating">
              ${[1, 2, 3, 4, 5].map((i) => `<span data-v="${i}" role="radio" aria-label="${i} star">${STAR.replace("%C", "star-off")}</span>`).join("")}
            </div>
          </div>
          <div class="field"><label>Title (optional)</label><input class="f-title" maxlength="200"></div>
          <div class="field"><label>Your review</label><textarea class="f-body" maxlength="5000"></textarea></div>
          <div class="field"><label>Name</label><input class="f-name" maxlength="80"></div>
          <div class="field"><label>Email \u2014 for verified-buyer check, never shown publicly</label><input class="f-email" type="email" maxlength="160" autocomplete="email"></div>
          <input class="hp" tabindex="-1" autocomplete="off" aria-hidden="true" placeholder="Company">
          <div class="form-actions">
            <button class="submit">Submit review</button>
            ${this.formFirst ? "" : `<button class="cancel" type="button">Cancel</button>`}
          </div>
          <div class="msg"></div>
        </div>`;

      const paint = (n) => host.querySelectorAll(".starpick span").forEach((sp) => {
        sp.innerHTML = STAR.replace("%C", Number(sp.dataset.v) <= n ? "star-on" : "star-off");
      });
      host.querySelectorAll(".starpick span").forEach((sp) => {
        sp.addEventListener("click", () => { this.formRating = Number(sp.dataset.v); paint(this.formRating); });
      });
      const cancelBtn = host.querySelector(".cancel");
      if (cancelBtn) cancelBtn.addEventListener("click", () => this.toggleForm());
      host.querySelector(".submit").addEventListener("click", () => this.submitForm(host));
    }

    async submitForm(host) {
      const msg = host.querySelector(".msg");
      const val = (sel) => host.querySelector(sel).value.trim();
      const ERR = {
        invalid_rating: "Please choose a star rating.", empty_body: "Please write your review.",
        missing_name: "Please add your name.", bad_email: "That email doesn\u2019t look right.",
        body_too_long: "That review is a bit too long.", rate_limited: "You\u2019ve submitted a few reviews recently \u2014 please try again later.",
      };
      msg.className = "msg";
      if (!this.formRating) { msg.className = "msg bad"; msg.textContent = ERR.invalid_rating; return; }
      if (!val(".f-body")) { msg.className = "msg bad"; msg.textContent = ERR.empty_body; return; }
      if (!val(".f-name")) { msg.className = "msg bad"; msg.textContent = ERR.missing_name; return; }

      const btn = host.querySelector(".submit");
      btn.disabled = true; msg.textContent = "Submitting\u2026";
      const payload = {
        product_id: this.productId, rating: this.formRating,
        title: val(".f-title"), body: val(".f-body"),
        author_name: val(".f-name"), author_email: val(".f-email"),
        company: host.querySelector(".hp").value,
      };
      try {
        const r = await fetch(this.apiBase + "/reviews-submit", {
          method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(payload),
        });
        const data = await r.json();
        if (data.ok) {
          host.querySelector(".form").outerHTML = `
            <div class="thanks">
              <h4>Thank you${val(".f-name") ? ", " + esc(val(".f-name")) : ""}!</h4>
              <p>Your review has been submitted and will appear once we\u2019ve approved it.${data.verified ? " You\u2019re a verified buyer \u2713" : ""}</p>
            </div>`;
        } else {
          btn.disabled = false; msg.className = "msg bad"; msg.textContent = ERR[data.error] || "Something went wrong. Please try again.";
        }
      } catch (e) {
        btn.disabled = false; msg.className = "msg bad"; msg.textContent = "Couldn\u2019t submit \u2014 please check your connection and try again.";
        console.error("[08L reviews] submit", e);
      }
    }

    card(r) {
      const photos = (r.photo_urls || []).length
        ? `<div class="photos">${r.photo_urls.map((u) =>
            `<a href="${esc(u)}" target="_blank" rel="noopener"><img loading="lazy" src="${esc(u)}" alt=""></a>`).join("")}</div>`
        : "";
      const reply = r.reply
        ? `<div class="reply"><div class="who">08 Left \u2014 Response</div><p>${esc(r.reply.trim())}</p></div>`
        : "";
      const verified = r.verified
        ? `<span class="verified"><svg viewBox="0 0 20 20"><path d="M8 13.2l-3.2-3.2-1.4 1.4L8 16l8-8-1.4-1.4z"/></svg>Verified buyer</span>`
        : "";
      const loc = r.location ? `<div class="rev-loc">${esc(locShort(r.location))}</div>` : "";
      return `
        <div class="rev">
          <div class="rev-head">
            <div class="rev-who"><span class="rev-name">${esc(r.author_name)}</span>${verified}</div>
            <span class="rev-date">${fmtDate(r.created_at)}</span>
          </div>
          <div class="rev-stars">${starsHTML(r.rating)}</div>
          ${r.title ? `<p class="rev-title">${esc(r.title)}</p>` : ""}
          ${r.body ? `<p class="rev-body">${esc(r.body)}</p>` : ""}
          ${photos}${reply}${loc}
        </div>`;
    }

    // Rich snippets so Google can show star ratings. Injected into the light
    // DOM (Googlebot renders JS and reads injected JSON-LD).
    injectJsonLd() {
      // 2026-10-30: OFF. The theme now prints ratings in the HTML (Organization in
      // header.liquid = store-wide; Product in 08l-product.liquid = that product's own
      // reviews, from the reviews-sync job). This used to add a second Product with the
      // STORE-wide 4.98 on every product page, which breaks Google's rule that a
      // product's rating must be about that product.
      return;
      const s = this.summary;
      if (!s || !s.count) return;
      const id = "el-reviews-jsonld" + (this.productId ? "-" + this.productId : "-store");
      document.getElementById(id)?.remove();
      const reviewObjs = this.reviews.slice(0, 10).map((r) => ({
        "@type": "Review",
        reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
        author: { "@type": "Person", name: r.author_name },
        datePublished: (r.created_at || "").slice(0, 10),
        reviewBody: r.body || undefined,
      }));
      const agg = { "@type": "AggregateRating", ratingValue: s.average, reviewCount: s.count, bestRating: 5, worstRating: 1 };
      const ld = this.productId
        ? { "@context": "https://schema.org", "@type": "Product",
            name: document.title.split(/[|\u2013-]/)[0].trim() || "Product",
            aggregateRating: agg, review: reviewObjs }
        : { "@context": "https://schema.org", "@type": "Organization",
            name: "08 Left", aggregateRating: agg };
      const tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.id = id;
      tag.textContent = JSON.stringify(ld);
      document.head.appendChild(tag);
    }
  }

  customElements.define("eight-left-reviews", EightLeftReviews);
})();
